import { LitElement, html, css, PropertyValues } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-toggle-button/src/voy-toggle-button';
import { VesselPositionModel } from '../../../src/models/vessel-position-model';
import { VoyToggleButton } from '../../voy-toggle-button/src/voy-toggle-button';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { VoyLatLongInput } from '../../voy-lat-long-input/src/voy-lat-long-input';
import { OnlineStatusController } from '../../../src/utils/online-status-controller';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;
@customElement('voy-vessel-position-popup')
export class VoyVesselPositionPopup extends LitElement {
    @property({ type: Boolean }) shown: boolean = false;
    private onlineStatus = new OnlineStatusController(this);

    static get styles() {
        return [
            css`
            :host {
            }

            .container {
                display: grid;
                margin: 15px;
                grid-template-columns: auto auto auto auto;
                align-items: center;
                grid-gap: 15px;
                font-size: 14px;
                font-family: MaerskText;
                color: var(--neutrals-grey-02, black);
            }

            voy-toggle-button {
                grid-column: span 4;
            }

            .alert {
                grid-column: span 4;
                display: flex;
                align-items: center;
                background: var(--voy-alert-message-background-color, white);
                border: 1px solid var(--voyage-error-red, red);
                color: var(--voyage-error-red, red);
                box-sizing: border-box;
                border-radius: 3px;
                padding: 10px;
                white-space-collapse: preserve;
            }

            svg {
                height: 14px;
                display: block;
                fill: currentColor;
                margin-right: 10px;
            }

            .disabled {
                opacity: 0.6;
            }
            `
        ];
    }

    render() {
        const disableInput = this._usingGPS || (isVesselOnCloud && !this.onlineStatus.isOnline)
        const enableAcceptButton = this._inputValid || (isVesselOnCloud ? this.onlineStatus.isOnline : this._usingGPS);
        const offlineText = isVesselOnCloud && !this.onlineStatus.isOnline ? '\nYou must be online to update position. Currently offline.' : '';

        let alertMessage = '';
        const toggleLabel = isVesselOnCloud ? "Use integrated AIS position" : "Use integrated GPS input";

        if (this._malfunctioningGPS) {
            const ageText = this._getPositionAge();
            alertMessage = isVesselOnCloud
                ? `Integrated AIS position source is outdated${ageText}. Please enter your current position below. ${offlineText}`
                : 'Integrated GPS source is not working; Please enter your current position below';
        } else if (!this._usingGPS) {
            alertMessage = `Using manually entered position; Please enter your current position below ${offlineText}`;
        }

        return html`
        <voyage-popup accept-button-text="Save" .shown=${this.shown}
                      @accept=${this._save}
                      @close=${() => this.dispatchEvent(new Event('close'))}
                      header-text="Vessel Position"
                      .disableAcceptButton=${!enableAcceptButton}>
            <div class="container">
            
                <voy-toggle-button label=${toggleLabel} .disabled=${isVesselOnCloud ? !this.onlineStatus.isOnline : false} .checked=${this._usingGPS}
                               @input=${this._toggleUsingGPS}
                ></voy-toggle-button>

                ${this._malfunctioningGPS || !this._usingGPS ? html`
                <div class="alert">${SvgIcons.lightExclamationTriangle} ${alertMessage}</div>
                ` : html``}
                
                <div class=${disableInput ? "disabled" : ""}>Latitude</div>
                <voy-lat-long-input class=${disableInput ? "disabled" : ""} .value=${this._manualLat} .disabled=${disableInput}
                                    @input=${this._onInput}>
                </voy-lat-long-input>

                <div class=${disableInput ? "disabled" : ""}>Longitude</div>
                <voy-lat-long-input long class=${disableInput ? "disabled" : ""} .value=${this._manualLong} .disabled=${disableInput}
                                    @input=${this._onInput}>
                </voy-lat-long-input>

            </div>
        </voyage-popup>
        `;
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("shown") && this.shown) {
            this._onShown();
        }
    }

    @property({ attribute: false }) private _usingGPS: boolean = false; // GPS, if on cloud it refers to AIS.
    @property({ attribute: false }) private _malfunctioningGPS: boolean = false;
    @property({ attribute: false }) private _manualLat: number | null = null;
    @property({ attribute: false }) private _manualLong: number | null = null;
    @property({ attribute: false }) private _inputValid: boolean = true;

    private async _onShown() {
        this._usingGPS = !VesselPositionModel!.usingManualPositionOverride;
        
        // Check if any non-manual source is outdated
        const hasOutdatedNonManualSource = this._checkIfPositionOutdated();
        this._malfunctioningGPS = VesselPositionModel!.malfunctioningGPS || hasOutdatedNonManualSource;

        const position = VesselPositionModel!.position;
        if (position) {
            this._manualLat = position.latitude;
            this._manualLong = position.longitude;
        }
        else {
            this._manualLat = null;
            this._manualLong = null;
        }
        this._checkValidState();
    }

    private _toggleUsingGPS(evt: InputEvent) {
        const target = <VoyToggleButton>evt.target;
        this._usingGPS = target.checked;
        if (this._usingGPS) {
            const position = VesselPositionModel!.position;
            if (position) {
                this._manualLat = position.latitude;
                this._manualLong = position.longitude;
            }
            else {
                this._manualLat = null;
                this._manualLong = null;
            }
        }
        this._checkValidState();
    }

    private _onInput(evt: InputEvent) {
        const target = <VoyLatLongInput>evt.target;
        if (target.long) {
            this._manualLong = target.value;
        }
        else {
            this._manualLat = target.value;
        }
        this._checkValidState();
    }

    private _checkValidState() {
        const inputs = <NodeListOf<VoyLatLongInput>>this.shadowRoot!.querySelectorAll('voy-lat-long-input');
        this._inputValid = !inputs[0].invalid && !inputs[1].invalid;
    }

    private _checkIfPositionOutdated(): boolean {
        const position = VesselPositionModel!.position;
        if (!position || !position.dataSources) return false;

        const outOfDateTime = 1000 * 60 * 30; // 30 minutes grace period
        let latestNonManualTimestamp: string | null = null;

        // Find the latest timestamp from all non-manual sources
        for (const [sourceName, sourceData] of Object.entries(position.dataSources)) {
            if (sourceName.toLowerCase() !== 'manual' && sourceData.timestamp) {
                if (!latestNonManualTimestamp || sourceData.timestamp > latestNonManualTimestamp) {
                    latestNonManualTimestamp = sourceData.timestamp;
                }
            }
        }

        // If we found a non-manual source, check if it's outdated
        if (latestNonManualTimestamp) {
            const positionTime = new Date(latestNonManualTimestamp).getTime();
            const isOutdated = positionTime + outOfDateTime < Date.now();
            return isOutdated;
        }

        // No non-manual sources found
        return true;
    }

    private _getPositionAge(): string {
        const position = VesselPositionModel!.position;
        if (!position) return '';

        try {
            let latestTimestamp: string | null = null;

            // Check dataSources for non-manual sources
            if (position.dataSources) {
                for (const [sourceName, sourceData] of Object.entries(position.dataSources)) {
                    if (sourceName.toLowerCase() !== 'manual' && sourceData.timestamp) {
                        if (!latestTimestamp || sourceData.timestamp > latestTimestamp) {
                            latestTimestamp = sourceData.timestamp;
                        }
                    }
                }
            }

            // Fallback to main timestamp if no non-manual sources found
            if (!latestTimestamp && position.timestamp) {
                latestTimestamp = position.timestamp;
            }

            if (!latestTimestamp) return '';

            const positionTime = new Date(latestTimestamp).getTime();
            const now = Date.now();
            const ageMs = now - positionTime;

            if (ageMs < 0) return ''; // Future timestamp, ignore

            const ageMinutes = Math.floor(ageMs / 60000);
            const ageHours = Math.floor(ageMinutes / 60);
            const ageDays = Math.floor(ageHours / 24);

            if (ageDays > 0) {
                return ` (${ageDays} day${ageDays > 1 ? 's' : ''} old)`;
            } else if (ageHours > 0) {
                return ` (${ageHours} hour${ageHours > 1 ? 's' : ''} old)`;
            } else if (ageMinutes > 0) {
                return ` (${ageMinutes} minute${ageMinutes > 1 ? 's' : ''} old)`;
            }
            return ' (less than 1 minute old)';
        } catch (e) {
            return '';
        }
    }

    private async _save() {
        if (!this._usingGPS) {
            await VesselPositionModel!.setManualPosition([this._manualLong!, this._manualLat!]);
        }
        await VesselPositionModel!.setManualPositionOverride(!this._usingGPS);
        this.dispatchEvent(new Event('close'));
    }
};
